import { resolveMergedGroupDisplayState } from "./authoring-recommendation-groups"
import { getAuthoringRecommendationState } from "./authoring-schedule-recommendations"

const TASK_FIELD_SUFFIXES = {
    schedule_title: "title",
    intro_content: "content",
    intro_note: "note",
    task_title: "title",
    task_content: "content",
    task_note: "note",
    task_frequency: "frequency",
}

/**
 * @param {object} recommendation
 * @returns {string[]}
 */
export function recommendationToEditScopes(recommendation) {
    if (!recommendation) return []

    const changeType = `${recommendation?.changeType || ""}`
    const fieldSuffix = TASK_FIELD_SUFFIXES[changeType]
    const scopes = []

    if (changeType === "schedule_title") {
        scopes.push("schedule:title")
    }

    if (changeType === "intro_content") {
        scopes.push("intro:content")
    }

    if (changeType === "intro_note") {
        scopes.push("intro:note")
    }

    if (fieldSuffix) {
        addTaskFieldScopes({
            scopes,
            identifiers: collectRecommendationTaskScopeIdentifiers(recommendation),
            fieldSuffix,
            includeWildcard: changeType === "remove_task",
        })
    }

    const taskNumber = Number(recommendation?.taskNumber)
    if (fieldSuffix && Number.isInteger(taskNumber) && taskNumber > 0) {
        scopes.push(`taskNumber:${taskNumber}:${fieldSuffix}`)
        if (changeType === "remove_task") {
            scopes.push(`taskNumber:${taskNumber}:*`)
        }
    }

    return scopes
}

/**
 * @param {object} params
 * @param {string} params.field
 * @param {object} params.target
 * @param {object} params.draft
 * @returns {string[]}
 */
export function resolveEditScopesFromFieldUpdate({ field, target, draft }) {
    if (!draft || !target || !field) return []

    const normalizedField = normalizeFieldPath(field)

    if (target === draft) {
        return resolveScheduleRootFieldScopes(normalizedField)
    }

    if (target === draft.intro) {
        return resolveIntroFieldScopes(normalizedField)
    }

    const task = findDraftTaskByReference(draft, target)
    if (task) {
        return resolveTaskFieldScopes({ draft, task, field: normalizedField })
    }

    return resolveTaskFieldScopesForLooseTarget({ draft, target, field: normalizedField })
}

/**
 * @param {object[]} recommendations
 * @returns {{ byScope: Map<string, Set<string>>, displayIndexByRecommendationId: Map<string, number>, displayIndexByMergeGroupKey: Map<string, number> }}
 */
export function buildAuthoringRecommendationDisplayIndex(recommendations = [], displayItems = []) {
    const byScope = new Map()
    const displayIndexByRecommendationId = new Map()
    const displayIndexByMergeGroupKey = new Map()

    for (const recommendation of Array.isArray(recommendations) ? recommendations : []) {
        if (!recommendation?.id) continue

        for (const scope of recommendationToEditScopes(recommendation)) {
            if (!byScope.has(scope)) byScope.set(scope, new Set())
            byScope.get(scope).add(recommendation.id)
        }
    }

    displayItems.forEach((item, index) => {
        if (item?.kind === "merged-group") {
            if (item.groupKey) displayIndexByMergeGroupKey.set(item.groupKey, index)
            for (const recommendation of item.items ?? []) {
                if (recommendation?.id) displayIndexByRecommendationId.set(recommendation.id, index)
            }
            return
        }

        if (item?.id) displayIndexByRecommendationId.set(item.id, index)
    })

    return { byScope, displayIndexByRecommendationId, displayIndexByMergeGroupKey }
}

/**
 * Patch only display rows affected by edit scopes (conflict state refresh — no diff3 merge).
 *
 * @returns {object[]}
 */
export function patchAuthoringRecommendationDisplayItems({
    items = [],
    draft,
    index,
    scopes = [],
    selectedRecommendationIdsByGroupKey = {},
}) {
    if (!items.length || !index || !scopes.length || !draft) return items

    const affectedIndices = collectAffectedDisplayIndices({ index, scopes })
    if (!affectedIndices.size) return items

    const next = items.slice()

    for (const displayIndex of affectedIndices) {
        const item = items[displayIndex]
        if (!item) continue

        if (item.kind === "merged-group") {
            next[displayIndex] = {
                ...item,
                groupState: resolveMergedGroupDisplayState({
                    item,
                    draft,
                    selectedRecommendationIdsByGroupKey,
                }),
            }
            continue
        }

        next[displayIndex] = {
            ...item,
            displayState: getAuthoringRecommendationState({ recommendation: item, draft }),
        }
    }

    return next
}

/**
 * @param {string} eventName
 * @returns {string}
 */
export function fieldPathFromFieldUpdateEvent(eventName) {
    const prefix = "Field.Update."
    if (!eventName || !`${eventName}`.startsWith(prefix)) return ""
    return `${eventName}`.slice(prefix.length)
}

function collectAffectedDisplayIndices({ index, scopes }) {
    const affectedRecommendationIds = new Set()

    for (const scope of scopes) {
        collectScopeMatches(index.byScope, scope, affectedRecommendationIds)
    }

    const affectedIndices = new Set()

    for (const recommendationId of affectedRecommendationIds) {
        const displayIndex = index.displayIndexByRecommendationId.get(recommendationId)
        if (displayIndex != null) affectedIndices.add(displayIndex)
    }

    return affectedIndices
}

function collectScopeMatches(byScope, scope, targetIds) {
    const direct = byScope.get(scope)
    if (direct) {
        for (const id of direct) targetIds.add(id)
    }

    if (!scope.endsWith(":*")) return

    const prefix = scope.slice(0, -1)
    for (const [candidateScope, ids] of byScope.entries()) {
        if (!candidateScope.startsWith(prefix)) continue
        for (const id of ids) targetIds.add(id)
    }
}

function resolveScheduleRootFieldScopes(field) {
    if (field === "title") return ["schedule:title"]
    if (field === "code") return ["schedule:code"]
    if (field === "intro.content" || field.startsWith("intro.content")) return ["intro:content"]
    if (field === "intro.note" || field.startsWith("intro.note")) return ["intro:note"]
    return []
}

function resolveIntroFieldScopes(field) {
    if (field === "content" || field.endsWith(".content")) return ["intro:content"]
    if (field === "note" || field.endsWith(".note")) return ["intro:note"]
    return []
}

function resolveTaskFieldScopes({ draft, task, field }) {
    const suffix = resolveTaskFieldScopeSuffix(field)
    if (!suffix) return []

    const scopes = []
    addTaskFieldScopes({
        scopes,
        identifiers: collectTaskScopeIdentifiers(task),
        fieldSuffix: suffix,
        includeWildcard: true,
    })

    const taskNumber = findTaskSequenceNumber(draft?.tasks, task)
    if (taskNumber) {
        scopes.push(`taskNumber:${taskNumber}:${suffix}`)
        scopes.push(`taskNumber:${taskNumber}:*`)
    }

    return scopes
}

function resolveTaskFieldScopesForLooseTarget({ draft, target, field }) {
    if (!target || typeof target !== "object") return []

    const suffix = resolveTaskFieldScopeSuffix(field)
    if (!suffix) return []

    const matchedTask = findDraftTaskByIdentifiers(draft, target)
    if (!matchedTask) return []

    return resolveTaskFieldScopes({ draft, task: matchedTask, field })
}

function addTaskFieldScopes({ scopes, identifiers, fieldSuffix, includeWildcard }) {
    for (const identifier of identifiers) {
        scopes.push(`task:${identifier}:${fieldSuffix}`)
        if (includeWildcard) {
            scopes.push(`task:${identifier}:*`)
        }
    }
}

export function collectTaskScopeIdentifiers(task) {
    if (!task) return []

    const identifiers = new Set()
    for (const value of [task?.taskId, task?.temporaryTaskId, task?.id, task?.headerTaskId]) {
        const identifier = `${value || ""}`.trim()
        if (identifier) identifiers.add(identifier)
    }

    return [...identifiers]
}

export function collectRecommendationTaskScopeIdentifiers(recommendation) {
    if (!recommendation) return []

    const identifiers = new Set()
    for (const value of [recommendation?.resolvedTaskId, recommendation?.taskId, recommendation?.headerTaskId]) {
        const identifier = `${value || ""}`.trim()
        if (identifier) identifiers.add(identifier)
    }

    return [...identifiers]
}

function resolveTaskFieldScopeSuffix(field) {
    if (field === "title" || field.endsWith(".title")) return "title"
    if (field === "content" || field.endsWith(".content")) return "content"
    if (field === "note" || field.endsWith(".note")) return "note"
    if (field.startsWith("frequency")) return "frequency"
    return ""
}

function normalizeFieldPath(field) {
    return `${field || ""}`.trim()
}

function findDraftTaskByReference(draft, target) {
    const tasks = Array.isArray(draft?.tasks) ? draft.tasks : []
    return tasks.find((task) => task === target) ?? null
}

function findDraftTaskByIdentifiers(draft, target) {
    const tasks = Array.isArray(draft?.tasks) ? draft.tasks : []
    const targetIdentifiers = collectTaskScopeIdentifiers(target)
    if (!targetIdentifiers.length) return null

    return (
        tasks.find((task) =>
            collectTaskScopeIdentifiers(task).some((identifier) => targetIdentifiers.includes(identifier))
        ) ?? null
    )
}

function findTaskSequenceNumber(tasks, targetTask) {
    if (!targetTask) return 0

    let counter = 0
    for (const task of Array.isArray(tasks) ? tasks : []) {
        if (task?.isHeader) continue
        counter += 1
        if (task === targetTask) return counter
    }

    return 0
}
